/* eslint-disable react/prop-types */
import { Divider, Icon, Tooltip } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { getUproColor } from "utils/colors";
import {
  formatStockQuantity,
  formatUnitQuantity,
  makeNiceText,
  normalizeStockByUnit,
} from "utils/formatters";

const getRows = (data, handleOpenMenu) => {
  let tipos = ["Material", "Consumible", "Recurso"];
  let icons = ["category", "recycling", "layers"];
  let colors = ["uproYellow", "uproGreen", "uproPink"];
  const rows = [];

  if (data) {
    data.forEach((item) => {
      const stockActual = Number(item?.stock?.cantidad || 0);
      const stockPrestamo = Number(item?.stock?.enPrestamo || 0);
      const stockReservado = Number(item?.stock?.reservado || 0);
      const stockDisponible = Number(item?.stock?.disponible ?? stockActual - stockReservado);
      const pendienteRecepcionar = Number(item?.pendienteRecepcionar || 0);
      const porcentajeUtilizable = Number(item?.porcentajeUtilizable || 0);
      const stockUtilizable = Math.trunc((stockActual * porcentajeUtilizable) / 100);
      const stockDesechos = stockActual - stockUtilizable;
      const stockTotal = normalizeStockByUnit(stockActual + stockPrestamo, item?.tipoUnidad);
      const stockTotalConPendiente = stockTotal + pendienteRecepcionar;

      rows.push({
        type: (
          <SoftBox
            display="flex"
            alignItems="center"
            sx={{
              cursor: "pointer",
            }}
            data={
              <Icon
                sx={{
                  verticalAlign: "middle",
                  cursor: "pointer",
                  color: item?.disabled
                    ? getUproColor()
                    : getUproColor(colors[tipos.indexOf(item?.tipo)]),
                }}
                fontSize="lg"
              >
                {icons[tipos.indexOf(item?.tipo)] || "layers"}
              </Icon>
            }
            onClick={(e) => handleOpenMenu(e, item)}
          >
            <SoftBox pl={3} mr={2}>
              <Tooltip
                title={item?.disabled ? "Recurso deshabilitado" : item?.tipo}
                placement="top"
              >
                <Icon
                  sx={{
                    verticalAlign: "middle",
                    cursor: "pointer",
                    color: item?.disabled
                      ? getUproColor()
                      : getUproColor(colors[tipos.indexOf(item?.tipo)]),
                  }}
                  fontSize="lg"
                >
                  {icons[tipos.indexOf(item?.tipo)] || "layers"}
                </Icon>
              </Tooltip>
            </SoftBox>
          </SoftBox>
        ),
        id: (
          <SoftBox
            sx={{
              cursor: "pointer",
            }}
            data={item?.id}
            onClick={(e) => handleOpenMenu(e, item)}
          >
            <SoftTypography
              verticalAlign="text-top"
              variant="caption"
              color="dark"
              fontWeight="bold"
            >
              {item?.id}
            </SoftTypography>
          </SoftBox>
        ),
        nombre: (
          <SoftBox
            sx={{
              cursor: "pointer",
            }}
            data={item?.nombre}
            onClick={(e) => handleOpenMenu(e, item)}
          >
            <SoftTypography
              verticalAlign="text-top"
              variant="caption"
              color="secondary"
              fontWeight="medium"
            >
              {item?.nombre.length > 70
                ? makeNiceText(item?.nombre).slice(0, 70) + "..."
                : makeNiceText(item?.nombre)}
              {item?.disabled && (
                <Tooltip title={item?.motivoBaja}>
                  <SoftTypography
                    verticalAlign="text-top"
                    variant="caption"
                    color="error"
                    fontWeight="medium"
                    sx={{ cursor: "pointer" }}
                  >
                    &nbsp; (Deshabilitado -{" "}
                    {item?.motivoBaja?.length > 50
                      ? item?.motivoBaja.slice(0, 50) + "..."
                      : item?.motivoBaja}
                    )
                  </SoftTypography>
                </Tooltip>
              )}
            </SoftTypography>
          </SoftBox>
        ),
        categoria: (
          <SoftBox
            sx={{
              cursor: "pointer",
            }}
            data={item?.categoria || ""}
            onClick={(e) => handleOpenMenu(e, item)}
          >
            <SoftTypography
              verticalAlign="text-top"
              variant="caption"
              color="dark"
              fontWeight="medium"
            >
              {item?.categoria || ""}
            </SoftTypography>
          </SoftBox>
        ),

        stock: (
          <Tooltip
            componentsProps={{
              tooltip: {
                sx: {
                  p: 1,
                  bgcolor: "common.white",
                  "& .MuiTooltip-arrow": {
                    color: "common.white",
                  },
                  boxShadow: 5,
                },
              },
            }}
            title={
              <>
                <SoftBox display="flex" justifyContent="space-between" gap={2}>
                  <SoftBox display="flex" flexDirection="column" textAlign="left">
                    <SoftTypography variant="caption">Stock fisico:</SoftTypography>
                    {stockReservado > 0 && (
                      <SoftTypography variant="caption">Reservado:</SoftTypography>
                    )}
                    <SoftTypography variant="caption">Disponible real:</SoftTypography>
                    {stockPrestamo > 0 && (
                      <SoftTypography variant="caption">En prestamo:</SoftTypography>
                    )}
                  </SoftBox>
                  <SoftBox display="flex" flexDirection="column" textAlign="right">
                    <SoftTypography variant="caption">
                      {formatStockQuantity(stockActual, item?.tipoUnidad)}
                    </SoftTypography>
                    {stockReservado > 0 && (
                      <SoftTypography variant="caption">
                        {formatStockQuantity(stockReservado, item?.tipoUnidad)}
                      </SoftTypography>
                    )}
                    <SoftTypography variant="caption" fontWeight="bold">
                      {formatStockQuantity(stockDisponible, item?.tipoUnidad)}
                    </SoftTypography>
                    {stockPrestamo > 0 && (
                      <SoftTypography variant="caption">
                        {formatStockQuantity(stockPrestamo, item?.tipoUnidad)}
                      </SoftTypography>
                    )}
                  </SoftBox>
                </SoftBox>
                {pendienteRecepcionar > 0 && (
                  <SoftBox display="flex" justifyContent="space-between" gap={2}>
                    <SoftBox display="flex" flexDirection="column" textAlign="left">
                      <SoftTypography variant="caption">A recibir:</SoftTypography>
                    </SoftBox>
                    <SoftBox display="flex" flexDirection="column" textAlign="right">
                      <SoftTypography variant="caption">
                        {formatUnitQuantity(pendienteRecepcionar, item?.tipoUnidad)}
                      </SoftTypography>
                    </SoftBox>
                  </SoftBox>
                )}
                <SoftBox display="flex" justifyContent="space-between" gap={2}>
                  <SoftBox display="flex" flexDirection="column" textAlign="left">
                    <SoftTypography variant="caption">Utilizable:</SoftTypography>
                  </SoftBox>
                  <SoftBox display="flex" flexDirection="column" textAlign="right">
                    <SoftTypography variant="caption">
                      {formatStockQuantity(stockUtilizable, item?.tipoUnidad)}
                    </SoftTypography>
                  </SoftBox>
                </SoftBox>
                <SoftBox display="flex" justifyContent="space-between" gap={2}>
                  <SoftBox display="flex" flexDirection="column" textAlign="left">
                    <SoftTypography variant="caption">Desechos:</SoftTypography>
                  </SoftBox>
                  <SoftBox display="flex" flexDirection="column" textAlign="right">
                    <SoftTypography variant="caption">
                      {formatStockQuantity(stockDesechos, item?.tipoUnidad)}
                    </SoftTypography>
                  </SoftBox>
                </SoftBox>
                <Divider sx={{ m: 1 }} />
                <SoftBox display="flex" justifyContent="space-between" gap={2}>
                  <SoftBox display="flex" flexDirection="column" textAlign="left">
                    <SoftTypography variant="caption">Total:</SoftTypography>
                    {item?.stock?.stockMinimo > 0 && (
                      <SoftTypography variant="caption">Mínimo:</SoftTypography>
                    )}
                  </SoftBox>
                  <SoftBox display="flex" flexDirection="column" textAlign="right">
                    <SoftTypography variant="caption" fontWeight="bold">
                      {formatUnitQuantity(stockTotalConPendiente, item?.tipoUnidad)}
                    </SoftTypography>
                    {item?.stock?.stockMinimo > 0 && (
                      <SoftTypography
                        variant="caption"
                        sx={
                          stockActual + stockPrestamo <= item?.stock?.stockMinimo
                            ? {
                                color: "red",
                                fontWeight: "bold",
                              }
                            : {}
                        }
                      >
                        ({item?.stock?.stockMinimo || 0})
                      </SoftTypography>
                    )}
                  </SoftBox>
                </SoftBox>
                <SoftBox display="flex" justifyContent="space-between" gap={2}>
                  <SoftBox display="flex" flexDirection="column" textAlign="left">
                    <SoftTypography variant="caption">Utilizable:</SoftTypography>
                  </SoftBox>
                  <SoftBox display="flex" flexDirection="column" textAlign="right">
                    <SoftTypography variant="caption">
                      {`${item?.porcentajeUtilizable}%`}
                    </SoftTypography>
                  </SoftBox>
                </SoftBox>
              </>
            }
          >
            <SoftBox
              sx={{
                cursor: "pointer",
              }}
              data={formatStockQuantity(stockActual, item?.tipoUnidad)}
              onClick={(e) => handleOpenMenu(e, item)}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <SoftTypography variant="caption" color="dark" fontWeight="medium">
                {stockPrestamo > 0 ? (
                  <>
                    {formatStockQuantity(stockActual, item?.tipoUnidad)}
                    &nbsp;(
                    {formatUnitQuantity(stockTotal, item?.tipoUnidad, { includeUnit: false })}
                    )
                  </>
                ) : (
                  formatStockQuantity(stockActual, item?.tipoUnidad)
                )}
              </SoftTypography>
              {item?.stock?.stockMinimo &&
                stockActual + stockPrestamo <= item?.stock?.stockMinimo && (
                  <>
                    &nbsp;&nbsp;
                    <Icon fontSize="small" color="error">
                      error_outline
                    </Icon>
                  </>
                )}
              {stockReservado > 0 && (
                <>
                  &nbsp;&nbsp;
                  <Icon fontSize="small" sx={{ color: getUproColor("uproBlue") }}>
                    lock_clock
                  </Icon>
                </>
              )}
              {pendienteRecepcionar > 0 && (
                <>
                  &nbsp;&nbsp;
                  <Icon fontSize="small" color="warning">
                    error_outline
                  </Icon>
                </>
              )}
            </SoftBox>
          </Tooltip>
        ),
        accion: (
          <SoftBox
            mr={2}
            sx={{
              cursor: "pointer",
            }}
            onClick={(e) => handleOpenMenu(e, item)}
            display="flex"
          >
            <Tooltip title="Ver detalle" placement="top">
              <Icon
                sx={{ color: getUproColor("uproGreen"), cursor: "pointer", mx: 0.5 }}
                fontSize="small"
                onClick={(e) => handleOpenMenu(e, item)}
              >
                chevron_right
              </Icon>
            </Tooltip>
          </SoftBox>
        ),
      });
    });
  }

  return rows;
};

const columns = [
  { name: "type", desc: " ", align: "left", width: "1%", noOrder: true },
  { name: "id", desc: "id", width: "2%", align: "left" },
  { name: "nombre", desc: "Nombre", align: "left" },
  { name: "categoria", desc: "Rubro", align: "left" },
  { name: "stock", desc: "Stock", align: "center", noOrder: true },
  { name: "accion", desc: " ", align: "center", width: "1%", noOrder: true },
];

export default { columns, getRows };
